/** A tenant's dashboard two-step sign-in (dashboard password MFA plan §3.2) — the tenant's
 *  "Sign-in security" tab.
 *
 *  The setting covers the dashboard's password sign-in only (SSO is untouched): **Optional**
 *  (users may set up an authenticator app or SMS code) or **Mandatory** (every password user
 *  sets one up at the next sign-in and passes it at every password sign-in, from the save that
 *  sets it — no grace). A franchise parent's Mandatory covers its licensees.
 *
 *  - `GET /tour_operators/{id}/dashboard_mfa/` (`console_top_view`): the mode, whether the
 *    tenant offers a password at all, the staff counts, the live password sessions a switch to
 *    Mandatory signs out, and the effect on MCP auto-apply.
 *  - `PATCH` `{mode, reason}` (`console_tenant_security_manage`): one config save carrying the
 *    reason (Configuration → Change history), audited; the answer adds what the change did. */
import { apiGet, apiPatch } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type DashboardMfaMode = Schemas['DashboardMfaMode'];
export type DashboardMfaPolicy = Schemas['DashboardMfaPolicy'];
export type TourOperatorDashboardMfa = Schemas['TourOperatorDashboardMfa'];
export type TourOperatorDashboardMfaPatch = Schemas['TourOperatorDashboardMfaPatch'];
export type TourOperatorDashboardMfaUpdated = Schemas['TourOperatorDashboardMfaUpdated'];
export type TourOperatorDashboardMfaError = Schemas['TourOperatorDashboardMfaError'];

/** The PATCH refuses a longer reason (`reason_too_long`). */
export const DASHBOARD_MFA_REASON_MAX = 500;

export function dashboardMfa(topId: number): Promise<TourOperatorDashboardMfa> {
  return apiGet(`/tour_operators/${topId}/dashboard_mfa/`);
}

export function updateDashboardMfa(
  topId: number,
  patch: TourOperatorDashboardMfaPatch,
): Promise<TourOperatorDashboardMfaUpdated> {
  return apiPatch(`/tour_operators/${topId}/dashboard_mfa/`, patch);
}

export const dashboardMfaKeys = {
  all: ['dashboardMfa'] as const,
  settings: (topId: number) => ['dashboardMfa', 'settings', topId] as const,
};

export const DASHBOARD_MFA_MODE_LABEL: Record<DashboardMfaMode, string> = {
  optional: 'Optional',
  mandatory: 'Mandatory',
};

/** "Optional" / "Mandatory" / "Mandatory (inherited from the franchise parent)". */
export function dashboardMfaPolicyLabel(policy: DashboardMfaPolicy): string {
  const label = DASHBOARD_MFA_MODE_LABEL[policy.mode] ?? policy.mode;
  return policy.inherited ? `${label} (inherited from the franchise parent)` : label;
}
